import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";
import { queryOptions } from "@tanstack/react-query";
import {
    type PageParams,
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import { zj } from "zod-joda";

export const reviewRequestAttributesSchema = z.object({
    createdAt: zj.zonedDateTime().transform((value) => value.withZoneSameInstant(localZoneId)),
    firstOpenedAt: zj
        .zonedDateTime()
        .transform((value) => value.withZoneSameInstant(localZoneId))
        .nullable(),
    reviewSubmittedAt: zj
        .zonedDateTime()
        .transform((value) => value.withZoneSameInstant(localZoneId))
        .nullable(),
    fullName: z.string(),
    emailAddress: z.string(),
});

const requestSelector = createDataSelector(
    createResourceSelector({
        type: "review_request",
        attributesSchema: reviewRequestAttributesSchema,
    }),
);

const requestsSelector = createResourceCollectionSelector({
    type: "review_request",
    attributesSchema: reviewRequestAttributesSchema,
});

export type ReviewRequest = ReturnType<typeof requestSelector>;
export type PaginatedReviewRequests = ReturnType<typeof requestsSelector>;

export type ListReviewRequestsOptions = {
    pageParams?: PageParams;
};

export const createReviewRequestQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListReviewRequestsOptions) =>
        queryOptions({
            queryKey: ["review-requests", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/review-requests");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return requestsSelector(await response.json());
            },
        }),
    get: (requestId: string) =>
        queryOptions({
            queryKey: ["review-request", requestId],
            queryFn: async ({ signal }) => {
                const response = await fetch(apiUrl(`/review-requests/${requestId}`), { signal });
                await handleJsonApiError(response);
                return requestSelector(await response.json());
            },
        }),
});
